import { useRef, useState } from "react";
import { Upload, X, Loader2, RotateCcw, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Single-image uploader with preview, replace, and clear.
 * Props:
 *   value         — current image URL (relative /api/files/... or absolute)
 *   onChange(url) — called with the new URL (empty string = cleared)
 *   defaultUrl    — optional default to show when value is empty (preview only)
 *   label         — small label shown above the box
 *   testid        — base data-testid
 *   aspect        — tailwind aspect class, defaults to "aspect-[4/5]"
 *   onClear       — optional explicit clear handler (for Page Image slots that
 *                   DELETE the override on the server to revert to default)
 */
export default function ImageUploader({ value, onChange, defaultUrl, label, testid = "img", aspect = "aspect-[4/5]", onClear }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = value || defaultUrl || "";
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const previewSrc = preview.startsWith("/api/") ? backendUrl + preview : preview;

  const pick = () => inputRef.current && inputRef.current.click();

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image is larger than 10 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    if (onClear) onClear();
    else onChange("");
  };

  return (
    <div data-testid={testid} className="w-full">
      {label && <div className="overline mb-2">{label}</div>}
      <div className={`relative ${aspect} border border-dashed border-[#C9C0AE] bg-[#FBF9F5] overflow-hidden group`}>
        {previewSrc ? (
          <img src={previewSrc} alt={label || ""} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#B0AFA8]">
            <ImageIcon size={32} />
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-[#1B3B36]/60 flex items-center justify-center text-[#F9F8F6]">
            <Loader2 className="animate-spin" />
          </div>
        )}
        {!uploading && (
          <div className="absolute inset-0 bg-[#1B3B36]/0 group-hover:bg-[#1B3B36]/55 transition-colors duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={pick}
              data-testid={`${testid}-upload`}
              className="inline-flex items-center gap-1 bg-[#F9F8F6] text-[#1B3B36] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] hover:bg-white"
            >
              <Upload size={12} /> {value ? "Replace" : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={clear}
                data-testid={`${testid}-clear`}
                className="inline-flex items-center gap-1 border border-[#F9F8F6] text-[#F9F8F6] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] hover:bg-[#F9F8F6] hover:text-[#1B3B36]"
              >
                <RotateCcw size={12} /> {defaultUrl ? "Revert" : "Remove"}
              </button>
            )}
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
    </div>
  );
}

/**
 * Multi-image uploader with reorder (via up/down arrows) and remove per tile.
 * Props:
 *   value: string[]        — list of image URLs
 *   onChange(list)          — called with updated list
 *   testid, label, maxCount
 */
export function MultiImageUploader({ value = [], onChange, label, testid = "gallery", maxCount = 20 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const pick = () => inputRef.current && inputRef.current.click();
  const toSrc = (u) => (u && u.startsWith("/api/") ? backendUrl + u : u);

  const uploadFiles = async (files) => {
    const remaining = Math.max(0, maxCount - value.length);
    const slice = files.slice(0, remaining);
    if (files.length > remaining) toast.message(`Added ${remaining} of ${files.length} — gallery limit reached`);
    setUploading(true);
    const next = [...value];
    for (const file of slice) {
      if (!file.type.startsWith("image/")) { toast.error(`${file.name}: not an image`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: over 10 MB`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(data.url);
      } catch (err) {
        toast.error(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    onChange(next);
    setUploading(false);
    if (next.length > value.length) toast.success(`Added ${next.length - value.length} image(s)`);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    await uploadFiles(files);
  };

  const move = (i, dir) => {
    const next = [...value];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const remove = (i) => {
    const next = value.filter((_, idx) => idx !== i);
    onChange(next);
  };

  const replace = (i) => {
    const el = document.createElement("input");
    el.type = "file";
    el.accept = "image/jpeg,image/png,image/webp";
    el.onchange = async (ev) => {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) return toast.error("Image is larger than 10 MB");
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const next = [...value];
        next[i] = data.url;
        onChange(next);
        toast.success("Image replaced");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
      }
    };
    el.click();
  };

  return (
    <div data-testid={testid}>
      {label && <div className="overline mb-2 flex items-center justify-between">
        <span>{label}</span>
        <span className="text-[11px] text-[#84968C] normal-case tracking-normal">{value.length}{maxCount ? `/${maxCount}` : ""}</span>
      </div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div key={`${url}-${i}`} data-testid={`${testid}-item-${i}`} className="relative aspect-square border border-[#D8D4CC] bg-[#FBF9F5] overflow-hidden group">
            <img src={toSrc(url)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 flex justify-between items-center px-1.5 py-1 bg-[#1B3B36]/80 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`${testid}-move-up-${i}`} className="text-[#F9F8F6] bg-[#1B3B36] disabled:opacity-30 text-[11px] px-1.5 py-0.5">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} data-testid={`${testid}-move-down-${i}`} className="text-[#F9F8F6] bg-[#1B3B36] disabled:opacity-30 text-[11px] px-1.5 py-0.5">↓</button>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => replace(i)} data-testid={`${testid}-replace-${i}`} className="text-[#F9F8F6] text-[10px] uppercase tracking-wider px-1.5">Replace</button>
                <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="text-[#F9F8F6]"><X size={13} /></button>
              </div>
            </div>
            <div className="absolute top-1 left-1 bg-[#1B3B36] text-[#F9F8F6] text-[10px] px-1.5 py-0.5">{i + 1}</div>
          </div>
        ))}
        {value.length < maxCount && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            data-testid={`${testid}-add`}
            className="aspect-square border border-dashed border-[#1B3B36] text-[#1B3B36] flex flex-col items-center justify-center gap-1 hover:bg-[#F0EDE5] disabled:opacity-50"
          >
            {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
            <span className="text-[10px] uppercase tracking-[0.18em]">Add image{value.length === 0 ? "s" : ""}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={onFiles} />
    </div>
  );
}
